/**
 * IntelligentSearchPage.js - AI-Powered Candidate Recommendation Engine
 * Features: Real-time streaming search, batch processing, bubble-sort animation for live results
 * State: streamingState (candidates[], progress, status), searchHistory with localStorage persistence
 * Key Functions: handleStreamingSearch() with createStreamingStateSetter(), handleSearch() for batch mode
 * Dependencies: IntelligentSearchService for API calls, JobRequirementsForm + CandidateRecommendations components
 */
import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { IntelligentSearchService } from "../../services/IntelligentSearchService";
import JobRequirementsForm from "./JobRequirementsForm";
import CandidateRecommendations from "./CandidateRecommendations";
import { toast } from "react-toastify";
import "./SemanticSearch.scss";

const IntelligentSearchPage = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [error, setError] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [completionStats, setCompletionStats] = useState(null);

  // Streaming state management
  const [streamingState, setStreamingState] = useState({
    isStreaming: false,
    candidates: [],
    progress: 0,
    status: "💡 Ready to analyze candidates with real-time streaming!",
    streamId: null
  });
  const [isStreamingMode, setIsStreamingMode] = useState(false);

  // Create a wrapped state setter
  const createStreamingStateSetter = () => (
    candidate,
    isStreaming,
    status,
    streamId,
    maxCandidates = 20 // Add maxCandidates parameter with fallback
  ) => {
    console.log("🔄 createStreamingStateSetter called with:", {
      hasCandidate: !!candidate,
      candidateName: candidate?.candidateName,
      isStreaming,
      status,
      streamId,
      maxCandidates
    });

    setStreamingState((prevState) => {
      console.log("📊 Previous streaming state:", {
        candidatesCount: prevState.candidates.length,
        isStreaming: prevState.isStreaming,
        status: prevState.status,
        streamId: prevState.streamId
      });

      // Start with previous state
      let newState = { ...prevState };

      // Update properties only if provided (not null/undefined)
      if (isStreaming !== null && isStreaming !== undefined) {
        newState.isStreaming = isStreaming;
      }
      if (streamId !== null && streamId !== undefined) {
        newState.streamId = streamId;
      }
      if (status !== null && status !== undefined) {
        newState.status = status;
      }

      if (candidate) {
        // Add candidate to the list (create new array to avoid mutation)
        const matchScore = parseFloat(
          candidate.aiScore ||
            candidate.matchScore ||
            candidate.relevanceScore ||
            0
        );

        const newCandidate = {
          ...candidate,
          matchScore: parseFloat(matchScore.toFixed(1)),
          // Ensure each candidate has a unique ID for React tracking
          candidateId:
            candidate.candidateId ||
            `streaming-candidate-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`
        };

        console.log("➕ Adding new candidate:", {
          name: newCandidate.candidateName,
          score: newCandidate.matchScore,
          aiScore: candidate.aiScore,
          originalMatchScore: candidate.matchScore,
          relevanceScore: candidate.relevanceScore,
          candidateId: newCandidate.candidateId
        });

        // Create new candidates array instead of mutating
        newState.candidates = [...prevState.candidates, newCandidate];
      }

      // Update progress based on current candidates length
      newState.progress = (newState.candidates.length / maxCandidates) * 100;

      console.log("📈 New streaming state:", {
        candidatesCount: newState.candidates.length,
        isStreaming: newState.isStreaming,
        status: newState.status,
        streamId: newState.streamId,
        progress: newState.progress
      });

      return newState;
    });
  };

  useEffect(() => {
    // Load search history from localStorage
    const savedHistory = localStorage.getItem("intelligentSearchHistory");
    if (savedHistory) {
      try {
        setSearchHistory(JSON.parse(savedHistory));
      } catch (err) {
        console.error("Error loading search history:", err);
      }
    }
  }, []);

  const handleSearch = async (formData) => {
    setStreamingState({
      isStreaming: false,
      candidates: [],
      progress: 0,
      status: "💡 Ready to analyze candidates with real-time streaming!",
      streamId: null
    });
    setCompletionStats(null);

    // Check if streaming mode is enabled
    if (isStreamingMode) {
      return handleStreamingSearch(formData);
    } // Original batch search logic
    setIsLoading(true);
    setError(null);
    setRecommendations(null);

    const startTime = Date.now();

    try {
      const response = await IntelligentSearchService.getCandidateRecommendations(
        formData
      );

      if (response && response.recommendations) {
        const endTime = Date.now();
        const actualProcessingTime = ((endTime - startTime) / 1000).toFixed(2);

        // Set completion stats for brief display
        setCompletionStats({
          candidatesFound: response.recommendations.length,
          processingTime: response.processingTime || `${actualProcessingTime}s`,
          actualProcessingTime: `${actualProcessingTime}s`
        });

        setIsLoading(false);
        setRecommendations(response);

        // Add to search history
        const newHistoryItem = {
          id: Date.now(),
          timestamp: new Date().toISOString(),
          jobRequirements: formData.jobRequirements,
          maxCandidates: formData.maxCandidates,
          company: formData.company,
          resultsCount: response.recommendations.length,
          processingTime: response.processingTime
        };

        const updatedHistory = [newHistoryItem, ...searchHistory.slice(0, 9)]; // Keep only last 10 searches
        setSearchHistory(updatedHistory);
        localStorage.setItem(
          "intelligentSearchHistory",
          JSON.stringify(updatedHistory)
        );

        toast.success(
          t("Search completed! Found {{count}} candidates in {{time}}", {
            count: response.recommendations.length,
            time: response.processingTime
          })
        );
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("Intelligent search error:", err);
      const errorMessage =
        err.response?.data ||
        err.message ||
        t("Search failed. Please try again.");
      setError(errorMessage);
      setCompletionStats(null);
      setIsLoading(false);
      toast.error(errorMessage);
    }
  };

  const handleStreamingSearch = async (formData) => {
    // Clear previous results
    setError(null);
    setRecommendations(null);

    console.log("🚀 Starting streaming search with formData:", formData);

    try {
      // Start streaming with integrated state management and bubble-up animation
      const streamId = await IntelligentSearchService.streamWithStateManagement(
        {
          jobRequirements: formData.jobRequirements,
          dataSource: formData.dataSource || 0, // Using database as default
          maxCandidates: formData.maxCandidates || 5, // Add maxCandidates
          additionalContext: formData.additionalContext || ""
        },
        createStreamingStateSetter() // Call the function to get the actual state setter
      );

      console.log("📡 Streaming started with streamId:", streamId);

      // Add to search history when streaming completes
      // This will be triggered automatically by the service's onComplete callback
      const newHistoryItem = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        jobRequirements: formData.jobRequirements,
        maxCandidates: formData.maxCandidates,
        company: formData.company,
        resultsCount: 0, // Will be updated when streaming completes
        processingTime: "Streaming",
        isStreaming: true
      };

      const updatedHistory = [newHistoryItem, ...searchHistory.slice(0, 9)];
      setSearchHistory(updatedHistory);
      localStorage.setItem(
        "intelligentSearchHistory",
        JSON.stringify(updatedHistory)
      );

      toast.info("🚀 Real-time candidate analysis started!");
    } catch (err) {
      console.error("Streaming search error:", err);
      const errorMessage =
        err.response?.data ||
        err.message ||
        t("Search failed. Please try again.");
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleStopStreaming = () => {
    IntelligentSearchService.stopStreamWithState(
      streamingState.streamId,
      setStreamingState // Pass the direct setter function
    );
    toast.info("⏹️ Streaming analysis stopped");
  };

  const handleClearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem("intelligentSearchHistory");
    toast.info(t("Search history cleared"));
  };

  const handleRestoreSearch = (historyItem) => {
    // This would trigger the form to be populated with the historical search
    // The form component would need to accept this as a prop
    setRecommendations(null);
    setError(null);
  };

  return (
    <div className="semantic-search-page">
      <Container fluid>
        <Row>
          <Col>
            <div className="page-header">
              <h1 className="page-title">
                <i className="fas fa-brain me-2"></i>
                {t("Intelligent Search")}
              </h1>
            </div>
          </Col>
        </Row>

        <Row>
          <Col lg={4} xl={3}>
            <Card className="search-form-card">
              <Card.Header>
                <h5 className="card-title">
                  <i className="fas fa-search me-2"></i>
                  {t("Intelligent Search")}
                </h5>
              </Card.Header>
              <Card.Body>
                {/* Streaming Mode Toggle */}
                <div className="mb-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="streamingModeToggle"
                      checked={isStreamingMode}
                      onChange={(e) => setIsStreamingMode(e.target.checked)}
                      disabled={isLoading || streamingState.isStreaming}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="streamingModeToggle"
                    >
                      <i className="fas fa-stream me-2"></i>
                      {isStreamingMode
                        ? "🚀 Real-time Streaming"
                        : "📊 Batch Search"}
                    </label>
                  </div>
                  <small className="text-dark">
                    {isStreamingMode
                      ? "Get live candidate analysis as results become available"
                      : "Get all results at once after processing completes"}
                  </small>
                </div>

                <JobRequirementsForm
                  onSubmit={handleSearch}
                  isLoading={isLoading || streamingState.isStreaming}
                  searchHistory={searchHistory}
                  onClearHistory={handleClearHistory}
                  onRestoreSearch={handleRestoreSearch}
                  isStreamingMode={isStreamingMode}
                  streamingState={streamingState}
                  onStopStreaming={handleStopStreaming}
                />
              </Card.Body>
            </Card>
          </Col>

          <Col lg={8} xl={9}>
            {error && (
              <Alert variant="danger" className="error-alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}

            {isLoading && (
              <Card className="loading-card">
                <Card.Body className="text-center">
                  {!completionStats ? (
                    <>
                      <div className="loader mb-3"></div>
                      <h5>{t("Searching for candidates...")}</h5>
                      <p className="text-dark">
                        {t(
                          "Analyzing candidate profiles using AI-powered intelligent matching"
                        )}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="completion-icon mb-3">
                        <i
                          className="fas fa-check-circle text-success"
                          style={{ fontSize: "3rem" }}
                        ></i>
                      </div>
                      <h5 className="text-success">Search Completed!</h5>
                      <div className="completion-stats mt-3">
                        <div className="row">
                          <div className="col-6">
                            <div className="stat-box">
                              <div className="stat-number text-primary">
                                {completionStats.candidatesFound}
                              </div>
                              <div className="stat-label text-dark">
                                Candidates Found
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="stat-box">
                              <div className="stat-number text-info">
                                {completionStats.processingTime}
                              </div>
                              <div className="stat-label text-dark">
                                Processing Time
                              </div>
                            </div>
                          </div>
                        </div>
                        {completionStats.actualProcessingTime &&
                          completionStats.actualProcessingTime !==
                            completionStats.processingTime && (
                            <div className="mt-2">
                              <small className="text-dark">
                                Total time:{" "}
                                {completionStats.actualProcessingTime}
                              </small>
                            </div>
                          )}
                      </div>
                      <p className="text-dark mt-3">
                        <i className="fas fa-spinner fa-spin me-2"></i>
                        Preparing results...
                      </p>
                    </>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Streaming Progress Display */}
            {streamingState.isStreaming && (
              <Card className="streaming-card mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">
                      <i className="fas fa-stream me-2"></i>
                      Live Streaming Analysis
                    </h5>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={handleStopStreaming}
                    >
                      <i className="fas fa-stop me-1"></i>
                      Stop Stream
                    </button>
                  </div>

                  <div className="mb-2">
                    <strong>Status:</strong> {streamingState.status}
                  </div>

                  {streamingState.progress > 0 && (
                    <div className="mb-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <small>
                          Progress: {Math.round(streamingState.progress)}%
                        </small>
                        <small>
                          {streamingState.candidates.length} candidates found
                          {streamingState.candidates.length > 0 && (
                            <span className="ms-2">
                              (Top score:{" "}
                              {Math.max(
                                ...streamingState.candidates.map(
                                  (c) => c.matchScore || 0
                                )
                              ).toFixed(1)}
                              )
                            </span>
                          )}
                        </small>
                      </div>
                      <div className="progress">
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated"
                          role="progressbar"
                          style={{ width: `${streamingState.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Display streaming candidates if available */}
            {streamingState.candidates.length > 0 && (
              <>
                {console.log("🎯 Rendering streaming candidates:", {
                  candidatesCount: streamingState.candidates.length,
                  candidates: streamingState.candidates.map((c) => ({
                    name: c.candidateName,
                    score: c.matchScore
                  }))
                })}
                <CandidateRecommendations
                  data={{
                    recommendations: streamingState.candidates,
                    processingTime: "Real-time streaming",
                    isStreaming: true
                  }}
                  onViewCandidate={(candidateUrl) => {
                    window.open(`/interview/view/${candidateUrl}`, "_blank");
                  }}
                />
              </>
            )}

            {recommendations && !isLoading && !streamingState.isStreaming && (
              <CandidateRecommendations
                data={recommendations}
                onViewCandidate={(candidateUrl) => {
                  // Handle viewing candidate details
                  window.open(`/interview/view/${candidateUrl}`, "_blank");
                }}
              />
            )}

            {!recommendations &&
              !isLoading &&
              !error &&
              !streamingState.isStreaming &&
              streamingState.candidates.length === 0 && (
                <Card className="welcome-card">
                  <Card.Body className="text-center">
                    <div className="welcome-icon">
                      <i className="fas fa-lightbulb"></i>
                    </div>
                    <h4>{t("Welcome to Intelligent Search")}</h4>
                    <p className="text-dark">
                      {t(
                        "Use AI-powered intelligent search to find the perfect candidates for your job requirements"
                      )}
                    </p>
                    <div className="welcome-features">
                      <div className="feature-item">
                        <i className="fas fa-robot"></i>
                        <span>{t("AI-Powered Analysis")}</span>
                      </div>
                      <div className="feature-item">
                        <i className="fas fa-bullseye"></i>
                        <span>{t("Precise Matching")}</span>
                      </div>
                      <div className="feature-item">
                        <i className="fas fa-chart-line"></i>
                        <span>{t("Smart Ranking")}</span>
                      </div>
                      <div className="feature-item">
                        <i className="fas fa-stream"></i>
                        <span>Real-time Streaming</span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default IntelligentSearchPage;
